import {
  completeInspection,
  deleteInspection,
  reopenInspection,
  saveInspection,
  useGetInspection,
  type InspectionDetailOut,
  type InspectionItemIn,
  type ItemCondition,
} from '@myrentalmate/api-client';
import { Alert, Button, Chip, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { okData } from '../../lib/apiData';
import { apiErrorMessage } from '../../lib/apiError';
import { formatDate, formatDateTime } from '../../lib/format';
import { useCan } from '../../lib/permissions';
import { RecordExtras } from '../records/RecordExtras';
import { byArea, CONDITION, KIND } from './inspectionLabels';

type Row = InspectionItemIn & {
  move_in_condition?: ItemCondition | null;
  move_in_notes?: string | null;
};

function Form({ view }: { view: InspectionDetailOut }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const canManage = useCan('maintenance.manage');
  const [items, setItems] = useState<Row[]>(view.items);
  const [date, setDate] = useState(view.inspected_on);
  const [notes, setNotes] = useState(view.notes ?? '');
  const [newArea, setNewArea] = useState('');
  const [newName, setNewName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const editing = canManage && view.status === 'draft';
  const compare = items.some((item) => item.move_in_condition);

  function change(index: number, patch: Partial<Row>) {
    setSaved(false);
    setItems((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await action();
      await queryClient.invalidateQueries();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const save = () =>
    saveInspection(view.id, {
      inspected_on: date,
      notes: notes.trim() || null,
      items: items.map(({ area, name, condition, notes: itemNotes }) => ({
        area,
        name,
        condition: condition ?? null,
        notes: itemNotes?.trim() || null,
      })),
    });

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}
      {saved && <Alert severity="success">Saved.</Alert>}
      {view.resident_ack_at && (
        <Alert severity="info">
          The resident acknowledged this on {formatDateTime(view.resident_ack_at)}.
          {view.resident_comments && ` They wrote: "${view.resident_comments}"`}
        </Alert>
      )}
      <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Button size="small" variant="outlined" href={`/api/inspections/${view.id}/pdf`}>
          Download PDF
        </Button>
        {editing && (
          <>
            <Button
              size="small"
              variant="outlined"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  await save();
                  setSaved(true);
                })
              }
            >
              Save
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  await save();
                  await completeInspection(view.id);
                })
              }
            >
              Complete
            </Button>
            <Button
              size="small"
              color="error"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  await deleteInspection(view.id);
                  navigate('/staff/maintenance/inspections');
                })
              }
            >
              Remove
            </Button>
          </>
        )}
        {canManage && view.status === 'completed' && !view.resident_ack_at && (
          <Button
            size="small"
            disabled={busy}
            onClick={() => void run(() => reopenInspection(view.id))}
          >
            Reopen
          </Button>
        )}
      </Stack>
      {editing && (
        <TextField
          type="date"
          label="Inspection Date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ maxWidth: 220 }}
        />
      )}
      {byArea(items.map((item, index) => ({ ...item, index }))).map(([area, rows]) => (
        <Paper key={area} variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {area}
          </Typography>
          <Stack spacing={1.5}>
            {rows.map((row) => (
              <Stack
                key={`${row.name}-${row.index}`}
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                sx={{ alignItems: { md: 'center' } }}
              >
                <Typography sx={{ flex: '1 1 30%' }}>{row.name}</Typography>
                {compare && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ flex: '0 0 150px' }}
                    title={row.move_in_notes ?? undefined}
                  >
                    Move-in:{' '}
                    {row.move_in_condition ? CONDITION[row.move_in_condition].label : 'not listed'}
                  </Typography>
                )}
                {editing ? (
                  <>
                    <TextField
                      select
                      size="small"
                      label="Condition"
                      value={row.condition ?? ''}
                      sx={{ flex: '0 0 150px' }}
                      slotProps={{
                        htmlInput: { 'aria-label': `Condition of ${area}: ${row.name}` },
                      }}
                      onChange={(e) =>
                        change(row.index, {
                          condition: (e.target.value || null) as ItemCondition | null,
                        })
                      }
                    >
                      {(Object.keys(CONDITION) as ItemCondition[]).map((value) => (
                        <MenuItem key={value} value={value}>
                          {CONDITION[value].label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      size="small"
                      label="Notes"
                      value={row.notes ?? ''}
                      sx={{ flex: '1 1 40%' }}
                      slotProps={{ htmlInput: { maxLength: 300 } }}
                      onChange={(e) => change(row.index, { notes: e.target.value })}
                    />
                  </>
                ) : (
                  <>
                    {row.condition ? (
                      <Chip
                        size="small"
                        color={CONDITION[row.condition].color}
                        label={CONDITION[row.condition].label}
                        sx={{ alignSelf: 'center' }}
                      />
                    ) : (
                      <span>–</span>
                    )}
                    <Typography variant="body2" sx={{ flex: '1 1 40%' }}>
                      {row.notes}
                    </Typography>
                  </>
                )}
              </Stack>
            ))}
          </Stack>
        </Paper>
      ))}
      {editing && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Add an Item
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField
              size="small"
              label="Area"
              value={newArea}
              onChange={(e) => setNewArea(e.target.value)}
            />
            <TextField
              size="small"
              label="Item"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
            <Button
              disabled={!newArea.trim() || !newName.trim()}
              onClick={() => {
                setItems((rows) => [
                  ...rows,
                  { area: newArea.trim(), name: newName.trim(), condition: null, notes: null },
                ]);
                setNewName('');
              }}
            >
              Add
            </Button>
          </Stack>
        </Paper>
      )}
      {editing ? (
        <TextField
          multiline
          minRows={2}
          label="Overall Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          slotProps={{ htmlInput: { maxLength: 1000 } }}
        />
      ) : (
        view.notes && <Typography sx={{ whiteSpace: 'pre-line' }}>{view.notes}</Typography>
      )}
      <RecordExtras entityType="inspection" entityId={view.id} canManage={canManage} />
    </Stack>
  );
}

export function InspectionDetailPage() {
  const id = Number(useParams().inspectionId);
  const view = okData(useGetInspection(id).data);
  if (!view) return null;
  return (
    <Stack spacing={2}>
      <div>
        <Typography variant="h5">
          {KIND[view.kind]} Inspection: {view.place}
        </Typography>
        <Typography color="text.secondary">
          {formatDate(view.inspected_on)} · {view.inspector_name} ·{' '}
          {view.status === 'draft'
            ? `${view.items_rated} of ${view.items_total} items checked`
            : 'Completed'}
        </Typography>
      </div>
      {/* A fresh form when it changes state, so it never edits stale rows. */}
      <Form key={`${view.id}-${view.status}`} view={view} />
    </Stack>
  );
}
